/**
 * The Data tab's stores that run in Docker (a compose file's mongo, an image service's postgres; vfs-service
 * src/data/compose): what shows when one does not run (Docker off, a compose file Flow does not run, a stopped app,
 * with Start: an explicit click only, nothing starts on its own), the bar over its data (where it is declared, its
 * restore points) and its restore points (save, restore with the project's name to confirm, delete).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ClockCounterClockwise, Play, Trash } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, ErrorState, Loading, errorText } from './DataCommon';
import { deleteDockerPoint, listDockerPoints, restoreDockerPoint, saveDockerPoint, startDockerStore } from '../../services/DataService';
import { dockerWhere, formatBytes, kindLabel, serviceLabel } from '../../utils/dataModel';

export function DockerStoppedState({ projectId, store, readOnly = false, onStarted = null }) {
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState(null);
  const where = dockerWhere(store);
  if (store.dockerStatus === 'docker-off') {
    return (
      <EmptyState title="Docker is off in this sandbox">
        {store.name} ({where}) runs in Docker. {store.reason || 'Turn on Docker in the sandbox to see its data.'}
      </EmptyState>
    );
  }
  if (store.dockerStatus === 'not-run') {
    return (
      <EmptyState title={`Flow does not run ${store.file}`}>
        {store.name} is declared there ({where}). Pin the file in Apps to run it with the project, then start it here.
      </EmptyState>
    );
  }
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await startDockerStore(projectId, store.id);
      setStarted(true);
      onStarted?.();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const app = serviceLabel(store.service);
  let action = null;
  if (started) {
    action = <p className="data-footnote" data-testid="docker-starting" role="status">Starting {app}… its data shows here once it runs (pulling an image can take a minute).</p>;
  } else if (readOnly) {
    action = <p className="data-footnote">You can view this project only: someone with editor access can start it.</p>;
  } else {
    action = (
      <button className="data-btn is-primary" data-testid="docker-start" disabled={busy} onClick={start} type="button">
        <Play aria-hidden size={15} />{busy ? 'Starting…' : `Start ${app}`}
      </button>
    );
  }
  return (
    <EmptyState action={<>{action}{error && <p className="data-error-text" role="alert">{error}</p>}</>} title={`${store.name} is not running`}>
      {kindLabel(store.kind)} {where}. {app} is stopped: start it to see and change its data.
    </EmptyState>
  );
}

DockerStoppedState.propTypes = { onStarted: PropTypes.func, projectId: PropTypes.string.isRequired, readOnly: PropTypes.bool, store: PropTypes.object.isRequired };

export function DockerStoreBar({ store, onOpenPoints = null }) {
  return (
    <div className="data-docker-bar" data-testid="docker-bar">
      <span>{dockerWhere(store)}{store.database ? <> · database <span className="data-mono">{store.database}</span></> : null}</span>
      {onOpenPoints && store.points && (
        <button className="data-btn is-ghost" data-testid="docker-open-points" onClick={onOpenPoints} type="button">
          <ClockCounterClockwise aria-hidden size={15} />Restore points
        </button>
      )}
    </div>
  );
}

DockerStoreBar.propTypes = { onOpenPoints: PropTypes.func, store: PropTypes.object.isRequired };

function RestoreDockerDialog({ projectId, projectName, store, point, onClose, onRestored }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const shownName = projectName || projectId;
  const matches = typed.trim().toLowerCase() === shownName.trim().toLowerCase() || typed.trim().toLowerCase() === projectId.toLowerCase();
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const answer = await restoreDockerPoint(projectId, store.id, point.id, typed.trim());
      onRestored(answer);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };
  return (
    <DataDialog
      footer={(
        <>
          <button className="data-btn" disabled={busy} onClick={onClose} type="button">Cancel</button>
          <button className="data-btn is-danger-solid" data-testid="docker-restore-confirm" disabled={!matches || busy} form="docker-restore" type="submit">{busy ? 'Restoring…' : 'Restore'}</button>
        </>
      )}
      onClose={onClose}
      subtitle={`${store.name} · ${dockerWhere(store)}`}
      testId="docker-restore-dialog"
      title={`Restore “${point.name}”`}
    >
      <form id="docker-restore" onSubmit={submit}>
        <p>The data of {store.name} is replaced by this copy. A restore point of the data as it is now is saved first.</p>
        <label className="data-label" htmlFor="docker-restore-name">
          Type the project’s name, <strong className="data-mono">{shownName}</strong>, to confirm
        </label>
        <input autoComplete="off" className="data-input is-wide" data-autofocus disabled={busy} id="docker-restore-name" onChange={(e) => setTyped(e.target.value)} type="text" value={typed} />
        {error && <p className="data-error-text" role="alert">{error}</p>}
      </form>
    </DataDialog>
  );
}

RestoreDockerDialog.propTypes = {
  onClose: PropTypes.func.isRequired, onRestored: PropTypes.func.isRequired, point: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired, projectName: PropTypes.string, store: PropTypes.object.isRequired,
};

const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export function DockerPoints({ projectId, projectName = null, store, readOnly = false, onBack = null, onRestored = null }) {
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setAnswer(await listDockerPoints(projectId, store.id)); } catch (e) { setError(errorText(e)); }
  }, [projectId, store.id]);
  useEffect(() => { load(); }, [load]);

  const save = async (event) => {
    event.preventDefault();
    if (!name.trim()) { return; }
    setBusy(true);
    setNote(null);
    try {
      const out = await saveDockerPoint(projectId, store.id, name.trim());
      setName('');
      setNote(`Saved “${out.point?.name || name.trim()}”.`);
      load();
    } catch (e) {
      setNote(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const points = answer?.points || [];
  const running = store.dockerStatus === 'running';
  let body;
  if (error && !answer) {
    body = <ErrorState error={error} onRetry={load} />;
  } else if (!answer) {
    body = <Loading label="Loading the restore points…" />;
  } else if (!answer.supported) {
    body = <EmptyState title="No restore points for this store yet">Restore points are available for PostgreSQL, MySQL, MongoDB and Redis in Docker; {kindLabel(store.kind)} is not covered yet.</EmptyState>;
  } else {
    body = (
      <>
        {!readOnly && (
          <form className="data-docker-save" onSubmit={save}>
            <label className="data-sr" htmlFor="docker-point-name">Name of the restore point</label>
            <input className="data-input" disabled={busy || !running} id="docker-point-name" maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Before the import" type="text" value={name} />
            <button className="data-btn is-primary" data-testid="docker-save-point" disabled={busy || !running || !name.trim()} type="submit">{busy ? 'Saving…' : 'Save a restore point'}</button>
          </form>
        )}
        {!running && <p className="data-footnote">{store.name} is not running: start it to save or restore a point.</p>}
        {note && <p className="data-footnote" role="status">{note}</p>}
        {!points.length
          ? <EmptyState title="No restore points yet">A point is saved before a change that can lose data, and whenever you save one.</EmptyState>
          : (
            <ul className="data-docker-points" data-testid="docker-points">
              {points.map((p) => (
                <li key={p.id}>
                  <span>
                    <strong>{p.name}</strong>
                    <small>{when(p.createdAt)}{p.sizeBytes ? ` · ${formatBytes(p.sizeBytes)}` : ''}{p.kind === 'pre-change' ? ' · saved before a change' : ''}</small>
                  </span>
                  {!readOnly && (
                    <span className="data-actions">
                      <button className="data-btn" disabled={!running} onClick={() => setRestoring(p)} type="button">Restore</button>
                      <button aria-label={`Delete ${p.name}`} className="data-btn is-ghost is-icon" onClick={() => setRemoving(p)} type="button"><Trash aria-hidden size={15} /></button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
      </>
    );
  }
  return (
    <section className="data-docker-points-panel" data-testid="docker-points-panel">
      <header className="data-docker-head">
        <h2>Restore points of {store.name}</h2>
        <p className="data-footnote">{dockerWhere(store)}. Copies of its data, taken with its own tools; kept with the project and in the sandbox&apos;s nightly backup.</p>
        {onBack && <button className="data-btn is-ghost" onClick={onBack} type="button">Back to the data</button>}
      </header>
      {body}
      {restoring && (
        <RestoreDockerDialog
          onClose={() => setRestoring(null)}
          onRestored={() => { setRestoring(null); setNote(`Restored “${restoring.name}”. A restore point of the data before it was saved.`); load(); onRestored?.(); }}
          point={restoring}
          projectId={projectId}
          projectName={projectName}
          store={store}
        />
      )}
      {removing && (
        <ConfirmDialog
          confirmLabel="Delete"
          onClose={() => setRemoving(null)}
          onConfirm={async () => { try { await deleteDockerPoint(projectId, store.id, removing.id); } finally { setRemoving(null); load(); } }}
          title={`Delete “${removing.name}”?`}
        >
          The copy is removed for good; the data of {store.name} does not change.
        </ConfirmDialog>
      )}
    </section>
  );
}

DockerPoints.propTypes = {
  onBack: PropTypes.func, onRestored: PropTypes.func, projectId: PropTypes.string.isRequired, projectName: PropTypes.string,
  readOnly: PropTypes.bool, store: PropTypes.object.isRequired,
};
